import type { ChangeEventHandler } from 'react'
import { useIntl } from '@/intl'

import { useDebouncedCallback } from '@/scopes/common/components/hooks/useDebouncedCallback'
import { HighlightedText } from '@/scopes/search/components/PartiallyHighlightedText'
import { useLiveSearch } from '@/scopes/search/search/domain/live-search/hooks'
import { useLiveSearchStore } from '@/scopes/search/search/infrastructure/live-search/store'
import { Item } from '@/scopes/search/search/pages/SearchPage/components/Header/Item'
import { space, tokPx } from '@sunwebgroup/ui-components'
import { IndeterminateProgressCircle, SearchField, Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { Category } from './Category'

const Wrapper = styled.div`
  &:not(:empty) {
    padding-top: ${space('primitives_space_16')};
    margin: ${space('primitives_space_16')};
    margin-top: 0;
  }
`

const BodyWrapper = styled.div`
  position: relative;
  &:not(:empty) {
    height: 100%;
    overflow-y: auto;
    border-radius: ${tokPx('semantics_overlays_corner_radius')};
    margin-top: ${space('primitives_space_8')};
    padding-top: ${space('primitives_space_16')};
  }
`

export function Loader() {
  const { isFetching, isLoading } = useLiveSearch()
  const { query } = useLiveSearchStore()
  if (isFetching || isLoading) {
    return (
      <>
        <HighlightedText>{query}</HighlightedText>
        {' ... '}
        <IndeterminateProgressCircle />
      </>
    )
  }
  return null
}

export function Body() {
  const { data, isFetching, isLoading } = useLiveSearch()
  if (!isFetching && !isLoading && !isEmpty(data) && data) {
    return <Results />
  }
  return null
}

export function NoResults() {
  const intl = useIntl()
  const { data, isFetching, isLoading } = useLiveSearch()
  const { query } = useLiveSearchStore()

  if (!isFetching && !isLoading && isEmpty(data) && query) {
    return (
      <>
        <Typography>{intl.formatMessage({ id: 'live-search_no_results' })}</Typography>
        {' '}
        <HighlightedText>{query}</HighlightedText>
      </>
    )
  }
  return null
}

function Results() {
  const intl = useIntl()
  const { data } = useLiveSearch()

  return (
    <>
      {data?.flightTickets && data.flightTickets.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_flighttickets' })}
          icon="AirplaneToTop"
          items={data.flightTickets}
        />
      )}
      {data?.destinations && data?.destinations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_destinations' })}
          icon="MapPin"
          items={data.destinations}
        />
      )}
      {data?.accommodations && data.accommodations.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_accommodations' })}
          icon="House"
          items={data.accommodations}
        />
      )}
      {data?.information && data.information.length > 0 && (
        <Category
          title={intl.formatMessage({ id: 'live-search_category_information' })}
          icon="InformationInCircle"
          items={data.information}
        />
      )}
    </>
  )
}

export function LiveSearch() {
  const [open, setOpen] = React.useState(false)
  const { setQuery } = useLiveSearchStore()
  const debouncedSetQuery = useDebouncedCallback(setQuery, 500)
  const intl = useIntl()

  const onOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen) {
        setQuery('')
      }
    },
    [setOpen],
  )

  const handleOnChange: ChangeEventHandler = (e) => {
    const { value } = e.target as HTMLInputElement
    debouncedSetQuery(value)
  }

  return (
    <Item open={open} onOpenChange={onOpenChange} a11yDescription={intl.formatMessage({ id: 'live-search_a11y_description' })} iconGlyph="Magnifier">
      <SearchField
        onChange={handleOnChange}
        placeholder={intl.formatMessage({ id: 'live-search_a11y_description' })}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        clearButtonAriaLabel={intl.formatMessage({ id: 'clear_search_button_aria_label' })}
      />
      <>
        <BodyWrapper>
          <Body />
        </BodyWrapper>
        <Wrapper>
          <Loader />
          <NoResults />
        </Wrapper>
      </>
    </Item>
  )
}
